
  import { Vue, Component, Prop } from 'vue-property-decorator';
  import { MappingSource } from '@/Sources';
  import { ISearchMapping } from '@/Interfaces';
  import { groupBy, IKeyValue } from '@samc/common';

  interface Item {
    id: string;
    name: string;
    isFolder: boolean;
  }

  interface HTMLSamcDropdownElement extends HTMLElement {
    forceUpdate(): void;
  }

  @Component({ name: 'mapping-dropdown-field' })
  export default class MappingDropdownField extends Vue {
    @Prop() public value!: string | null;
    @Prop({ default: 'Select Mapping...' }) public placeholder!: string;
    @Prop({ default: 1 }) public importProcessId!: number | number[];

    public loaded: boolean = false;
    public mappings: IKeyValue<string, ISearchMapping[]>[] = [];
    public search: string | null = null;

    public get val(): string | null { return this.value; }
    public set val(value: string | null) {
      this.$emit('input', value);
    }

    public get flattenedItems(): Item[] {
      let filteredMappings = this.mappings;
      if (this.search !== null) {
        const search = this.search;
        filteredMappings = this.mappings
          .map(x => ({ key: x.key, value: search.length > 0 ? x.value.filter(y => y.name.toLowerCase().includes(search.toLowerCase())) : x.value }))
          .filter(x => x.value.length > 0);
      }

      //this.dropdown()?.forceUpdate();
      return filteredMappings.reduce((a, x) => {
        a.push({ id: x.key, name: x.key, isFolder: true });
        return a.concat(x.value.map(y => ({ id: y.id.toString(), name: y.name, isFolder: false })));
      }, [] as Item[]);
    }

    public async mounted() {
      const source = new MappingSource({
        importProcessId: Array.isArray(this.importProcessId) ? undefined : this.importProcessId,
        importProcessIdList: Array.isArray(this.importProcessId) ? this.importProcessId : [this.importProcessId]
      }, 2147483647);

      await source.read();

      this.mappings = groupBy(source.items, x => x.group.name);
      this.mappings.sort((a, b) => a.key.localeCompare(b.key));
      this.loaded = true;
    }

    public dropdown(): HTMLSamcDropdownElement | undefined {
      return this.$refs.dropdown as HTMLSamcDropdownElement;
    }

    public onSearchChange(x: CustomEvent<string | null>) {
      this.search = x.detail;
    }

    public onOptionSelected(x: CustomEvent<any>) {
      const option = x.detail;
      
      // Only handle selections for non-folder items (actual mappings)
      if (option && !option.hasChildren) {
        // Try to get value from the option element's value attribute or property
        let selectedValue: string | null = null;
        
        // Method 1: Try to get from option.value property
        if (option.value !== undefined && option.value !== null) {
          selectedValue = option.value.toString();
        }
        // Method 2: Try to get from the element's value attribute
        else if (option.el) {
          const valueAttr = option.el.getAttribute('value');
          if (valueAttr) {
            selectedValue = valueAttr;
          }
        }
        // Method 3: Find the item in flattenedItems by text and get its ID
        if (!selectedValue && option.text) {
          const matchingItem = this.flattenedItems.find(item => item.name === option.text && !item.isFolder);
          if (matchingItem) {
            selectedValue = matchingItem.id;
          }
        }
        
        if (selectedValue) {
          this.val = selectedValue;
        }
      }
    }

    public onValueChange(x: CustomEvent<string | undefined>) {
      // Ignore null/undefined values (likely from folder clicks or clear)
      if (x.detail === null || x.detail === undefined) {       
        return;
      }
      
      // Check if the selected value is a folder (group name) instead of a mapping GUID
      // Folders have IDs that match group names, mappings have GUIDs
      const isFolder = this.mappings.some(m => m.key === x.detail);
      if (isFolder) {
        return;
      }
      
      // Only set if it's a valid GUID (mapping)
      this.val = x.detail;
    }
  }
